import { Button } from '@deepguide-ai/ui/button'
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@deepguide-ai/ui/select'
import { PAGE_SIZE_OPTIONS } from '@/lib/pagination'

type PaginationFooterBaseProps = {
  page: number
  pageSize: number
  onPageChange: (page: number) => void
  onPageSizeChange: (pageSize: number) => void
}

type TablePaginationFooterProps = PaginationFooterBaseProps &
  (
    | {
        totalItems: number
        itemCount?: never
        hasNextPage?: never
      }
    | {
        totalItems?: never
        itemCount: number
        hasNextPage: boolean
      }
  )

export function TablePaginationFooter(props: TablePaginationFooterProps) {
  const { page, pageSize, onPageChange, onPageSizeChange } = props
  const isCursorPagination = props.totalItems === undefined
  const totalPages = isCursorPagination
    ? null
    : Math.max(1, Math.ceil(props.totalItems / pageSize))
  const safePage = totalPages ? Math.min(page, totalPages) : page
  const startIndex = (safePage - 1) * pageSize
  const itemCount = isCursorPagination
    ? props.itemCount
    : Math.min(pageSize, Math.max(0, props.totalItems - startIndex))

  return (
    <div className='border-border flex items-center justify-between gap-3 border-t bg-card px-3 py-2'>
      <div className='flex items-center gap-3'>
        <div className='font-body text-muted-foreground text-[11px]'>
          {itemCount === 0
            ? 'No results'
            : isCursorPagination
              ? `Showing ${startIndex + 1}–${startIndex + itemCount}`
              : `Showing ${startIndex + 1}–${startIndex + itemCount} of ${props.totalItems}`}
        </div>
        <Select
          value={String(pageSize)}
          onValueChange={(v) => onPageSizeChange(Number(v))}
        >
          <SelectTrigger className='font-body border-border-divider text-text-body h-7 w-[112px] rounded-sm bg-card px-2 text-[11px]'>
            <SelectValue />
          </SelectTrigger>
          <SelectContent>
            {PAGE_SIZE_OPTIONS.map((size) => (
              <SelectItem key={size} value={String(size)}>
                {size} / page
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
      </div>
      <div className='flex items-center gap-2'>
        <Button
          variant='outline'
          size='sm'
          className='border-border-divider h-7 rounded-sm px-2.5 text-[11px]'
          onClick={() => onPageChange(Math.max(1, safePage - 1))}
          disabled={safePage === 1}
        >
          Previous
        </Button>
        <div className='font-body text-muted-foreground text-[11px]'>
          Page {safePage}
          {totalPages ? ` of ${totalPages}` : ''}
        </div>
        <Button
          variant='outline'
          size='sm'
          className='border-border-divider h-7 rounded-sm px-2.5 text-[11px]'
          onClick={() =>
            onPageChange(
              totalPages ? Math.min(totalPages, safePage + 1) : safePage + 1
            )
          }
          disabled={
            isCursorPagination ? !props.hasNextPage : safePage === totalPages
          }
        >
          Next
        </Button>
      </div>
    </div>
  )
}
